import {
  type ReactElement,
  type SyntheticEvent,
  useEffect,
  useId,
  useState,
} from 'react';
import useCopyToClipboard from 'react-use/esm/useCopyToClipboard';
import { useApi } from '@backstage/core-plugin-api';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import { catalogEntityDeletePermission } from '@backstage/plugin-catalog-common/alpha';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import IconButton from '@material-ui/core/IconButton';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Popover from '@material-ui/core/Popover';
import BugReportIcon from '@material-ui/icons/BugReport';
import CancelIcon from '@material-ui/icons/Cancel';
import FileCopyTwoToneIcon from '@material-ui/icons/FileCopyTwoTone';
import MoreVertIcon from '@material-ui/icons/MoreVert';

type VisibleType = 'visible' | 'hidden' | 'disable';

export interface EntityContextMenuExtraItem {
  /**
   * Pre-rendered icon element (e.g. `<MyIcon fontSize="small" />`). Using
   * `IconElement` keeps callers responsible for icon props and aligns with
   * the post-`IconComponent` Backstage API.
   */
  icon: ReactElement;
  onClick: () => void;
  title: string;
}

export interface EntityContextMenuProps {
  disableUnregister?: boolean | VisibleType;
  extraItems?: ReadonlyArray<EntityContextMenuExtraItem>;
  onInspectEntity: () => void;
  onUnregisterEntity: () => void;
}

const resolveUnregisterVisibility = (
  disableUnregister: boolean | VisibleType | undefined,
): VisibleType => {
  if (disableUnregister === undefined) return 'visible';
  if (disableUnregister === true) return 'disable';
  if (disableUnregister === false) return 'visible';
  return disableUnregister;
};

/**
 * EntityContextMenu — overflow menu for entity actions.
 *
 * Custom NS implementation (not vendored) replacing Backstage's
 * EntityContextMenu. Renders a 3-dots IconButton that opens a Popover
 * with: Copy URL, Inspect entity, Unregister entity, plus optional
 * extra items.
 *
 * The Unregister item is gated on
 * `catalogEntityDeletePermission` via `useEntityPermission` from
 * `@backstage/plugin-catalog-react/alpha`. When the permission is
 * denied (or while loading), the menu item is rendered disabled
 * (or hidden when `disableUnregister === 'hidden'`).
 */
export const EntityContextMenu = ({
  disableUnregister,
  extraItems,
  onInspectEntity,
  onUnregisterEntity,
}: EntityContextMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const reactId = useId();
  const popoverId = `ns-entity-context-menu-popover-${reactId}`;

  const toastApi = useApi(toastApiRef);
  const [copyState, copy] = useCopyToClipboard();
  const unregisterPermission = useEntityPermission(
    catalogEntityDeletePermission,
  );
  const unregisterVisibility = resolveUnregisterVisibility(disableUnregister);
  const unregisterAllowed =
    !unregisterPermission.loading && unregisterPermission.allowed;
  const unregisterDisabled =
    unregisterVisibility === 'disable' ||
    !unregisterAllowed ||
    unregisterPermission.loading;
  const unregisterHidden = unregisterVisibility === 'hidden';

  useEffect(() => {
    if (copyState.error) {
      toastApi.post({
        status: 'danger',
        timeout: 5000,
        title: 'Failed to copy URL',
      });
      return;
    }
    if (copyState.value) {
      toastApi.post({
        status: 'info',
        timeout: 5000,
        title: 'URL copied to clipboard',
      });
    }
  }, [copyState, toastApi]);

  const handleOpen = (event: SyntheticEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyUrl = () => {
    handleClose();
    copy(window.location.href);
  };
  const handleInspect = () => {
    handleClose();
    onInspectEntity();
  };
  const handleUnregister = () => {
    if (unregisterDisabled) {
      return;
    }
    handleClose();
    onUnregisterEntity();
  };

  return (
    <>
      <IconButton
        aria-controls={isOpen ? popoverId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Open entity actions"
        data-testid="ns-entity-context-menu-button"
        onClick={handleOpen}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        id={popoverId}
        onClose={handleClose}
        open={isOpen}
        PaperProps={{ style: { minWidth: 200 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuList autoFocusItem={isOpen}>
          <MenuItem
            key="copy-url"
            data-testid="ns-entity-context-menu-copy-url"
            onClick={handleCopyUrl}
          >
            <ListItemIcon>
              <FileCopyTwoToneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Copy URL" />
          </MenuItem>
          <MenuItem
            key="inspect-entity"
            data-testid="ns-entity-context-menu-inspect"
            onClick={handleInspect}
          >
            <ListItemIcon>
              <BugReportIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Inspect entity" />
          </MenuItem>
          {!unregisterHidden && (
            <MenuItem
              key="unregister-entity"
              aria-disabled={unregisterDisabled}
              data-testid="ns-entity-context-menu-unregister"
              disabled={unregisterDisabled}
              onClick={handleUnregister}
            >
              <ListItemIcon>
                <CancelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Unregister entity" />
            </MenuItem>
          )}
          {extraItems?.map(item => (
            <MenuItem
              key={item.title}
              onClick={() => {
                handleClose();
                item.onClick();
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.title} />
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
};
